import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { PageLayout } from "@/components/PageLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, Calendar, Tag, Edit, Clock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { SocialShareButtons } from "@/components/blog/SocialShareButtons";
import { useUserRole } from "@/hooks/useUserRole";
import { SEO } from "@/components/SEO";
import { Math as MathFormula } from "@/components/Math";
import { format } from "date-fns";

interface BlogPostData {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  content: string;
  cover_image_url: string | null;
  category: string;
  tags: string[];
  published: boolean;
  published_at: string;
  created_at: string;
  updated_at: string;
}

// Helper to parse inline formatting (bold, italic, links)
const parseInlineFormatting = (text: string): React.ReactNode[] => {
  const result: React.ReactNode[] = [];
  // Combined regex for links, bold, and italic
  const regex = /\[([^\]]+)\]\(([^)]+)\)|\*\*([^*]+)\*\*|\*([^*]+)\*/g;
  
  let lastIndex = 0;
  let match;
  let keyIndex = 0;
  
  while ((match = regex.exec(text)) !== null) {
    // Add text before match
    if (match.index > lastIndex) {
      result.push(text.slice(lastIndex, match.index));
    }
    
    if (match[1] && match[2]) {
      // Link: [text](url)
      result.push(
        <Link key={keyIndex++} to={match[2]} className="text-primary hover:underline">
          {match[1]}
        </Link>
      );
    } else if (match[3]) {
      // Bold: **text**
      result.push(<strong key={keyIndex++}>{match[3]}</strong>);
    } else if (match[4]) {
      // Italic: *text*
      result.push(<em key={keyIndex++}>{match[4]}</em>);
    }
    
    lastIndex = match.index + match[0].length;
  }
  
  // Add remaining text
  if (lastIndex < text.length) {
    result.push(text.slice(lastIndex));
  }
  
  return result.length > 0 ? result : [text];
};

// Helper to parse content with LaTeX
const parseContent = (text: string) => {
  const parts: Array<{ type: 'text' | 'inline' | 'display'; content: string }> = [];
  const displayRegex = /\$\$(.*?)\$\$/gs;
  const inlineRegex = /\$(.*?)\$/g;
  
  const displayParts = text.split(displayRegex);
  
  displayParts.forEach((part, index) => {
    if (index % 2 === 0) {
      const inlineParts = part.split(inlineRegex);
      inlineParts.forEach((inlinePart, inlineIndex) => {
        if (inlineIndex % 2 === 0) {
          if (inlinePart) {
            parts.push({ type: 'text', content: inlinePart });
          }
        } else {
          parts.push({ type: 'inline', content: inlinePart });
        }
      });
    } else {
      parts.push({ type: 'display', content: part });
    }
  });
  
  return parts;
};

// Parse a single line of markdown
const parseMarkdownLine = (line: string, key: string): React.ReactNode => {
  const trimmed = line.trim();
  
  // Headers
  if (trimmed.startsWith('# ')) {
    return <h1 key={key} className="text-3xl font-bold mt-10 mb-6">{parseInlineFormatting(trimmed.slice(2))}</h1>;
  }
  if (trimmed.startsWith('## ')) {
    return <h2 key={key} className="text-2xl font-bold mt-8 mb-4">{parseInlineFormatting(trimmed.slice(3))}</h2>;
  }
  if (trimmed.startsWith('### ')) {
    return <h3 key={key} className="text-xl font-semibold mt-6 mb-3">{parseInlineFormatting(trimmed.slice(4))}</h3>;
  }
  
  // Horizontal rule
  if (trimmed === '---' || trimmed === '***') {
    return <hr key={key} className="my-8 border-border" />;
  }
  
  // List items
  if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
    return (
      <li key={key} className="ml-6 mb-2 list-disc">
        {parseInlineFormatting(trimmed.slice(2))}
      </li>
    );
  }
  
  // Regular paragraph
  if (trimmed) {
    return <p key={key} className="mb-4 leading-relaxed">{parseInlineFormatting(trimmed)}</p>;
  }
  
  return null;
};

export default function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [post, setPost] = useState<BlogPostData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const { hasAccess } = useUserRole();
  const isAdmin = hasAccess(['admin']);

  useEffect(() => {
    const fetchPost = async () => {
      if (!slug) {
        setNotFound(true);
        setIsLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('slug', slug)
        .single();

      if (error || !data) {
        console.error('Error fetching post:', error);
        setNotFound(true);
      } else {
        // Only show if published or user is admin
        if (data.published || isAdmin) {
          setPost(data);
        } else {
          setNotFound(true);
        }
      }
      setIsLoading(false);
    };

    fetchPost();
  }, [slug, isAdmin]);

  if (isLoading) {
    return (
      <PageLayout>
        <div className="max-w-4xl mx-auto px-4 py-8">
          <Skeleton className="h-8 w-32 mb-8" />
          <Skeleton className="h-12 w-3/4 mb-4" />
          <Skeleton className="h-6 w-1/2 mb-8" />
          <Skeleton className="h-64 w-full mb-8" />
          <div className="space-y-4">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        </div>
      </PageLayout>
    );
  }

  if (notFound || !post) {
    return (
      <PageLayout>
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <h1 className="text-4xl font-bold mb-4">Post Not Found</h1>
          <p className="text-muted-foreground mb-8">
            The blog post you're looking for doesn't exist or has been removed.
          </p>
          <Button asChild>
            <Link to="/blog">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Blog
            </Link>
          </Button>
        </div>
      </PageLayout>
    );
  }

  const readingTime = Math.ceil(post.content.split(/\s+/).length / 200);

  // Group consecutive list items
  const renderContent = () => {
    const elements: React.ReactNode[] = [];
    
    parseContent(post.content).forEach((part, partIndex) => {
      if (part.type === 'display') {
        elements.push(<MathFormula key={`math-${partIndex}`} block>{part.content}</MathFormula>);
      } else if (part.type === 'inline') {
        elements.push(<MathFormula key={`inline-${partIndex}`}>{part.content}</MathFormula>);
      } else {
        // Parse text content line by line
        const lines = part.content.split('\n');
        let currentList: React.ReactNode[] = [];
        
        lines.forEach((line, lineIndex) => {
          const trimmed = line.trim();
          const isListItem = trimmed.startsWith('- ') || trimmed.startsWith('* ');
          
          if (isListItem) {
            currentList.push(parseMarkdownLine(line, `${partIndex}-${lineIndex}`));
          } else {
            // If we have accumulated list items, wrap them in ul
            if (currentList.length > 0) {
              elements.push(
                <ul key={`list-${partIndex}-${lineIndex}`} className="my-4">
                  {currentList}
                </ul>
              );
              currentList = [];
            }
            
            const parsed = parseMarkdownLine(line, `${partIndex}-${lineIndex}`);
            if (parsed) {
              elements.push(parsed);
            }
          }
        });
        
        // Don't forget remaining list items
        if (currentList.length > 0) {
          elements.push(
            <ul key={`list-${partIndex}-end`} className="my-4">
              {currentList}
            </ul>
          );
        }
      }
    });
    
    return elements;
  };

  return (
    <PageLayout>
      <SEO
        title={`${post.title} | CalcLab Blog`}
        description={post.excerpt}
        keywords={post.tags?.join(', ') || post.category}
      />

      <article className="max-w-4xl mx-auto px-4 py-8">
        {/* Back Button */}
        <div className="flex items-center justify-between mb-8">
          <Button variant="ghost" asChild>
            <Link to="/blog">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Blog
            </Link>
          </Button>
          {isAdmin && (
            <Button variant="outline" asChild>
              <Link to={`/admin/blog/${post.id}`}>
                <Edit className="h-4 w-4 mr-2" />
                Edit Post
              </Link>
            </Button>
          )}
        </div>

        {/* Draft Badge */}
        {!post.published && (
          <Badge variant="destructive" className="mb-4">Draft - Not Published</Badge>
        )}

        {/* Title and Meta */}
        <header className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Badge variant="secondary">{post.category}</Badge>
            <span className="flex items-center gap-1 text-sm text-muted-foreground">
              <Calendar className="h-3.5 w-3.5" />
              {format(new Date(post.published_at || post.created_at), 'MMMM d, yyyy')}
            </span>
            <span className="flex items-center gap-1 text-sm text-muted-foreground">
              <Clock className="h-3.5 w-3.5" />
              {readingTime} min read
            </span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4 leading-tight">
            {post.title}
          </h1>
          <p className="text-xl text-muted-foreground mb-6">
            {post.excerpt}
          </p>
          <SocialShareButtons 
            url={`https://calclab.app/blog/${post.slug}`}
            title={post.title}
            description={post.excerpt}
          />
        </header>

        {/* Cover Image */}
        {post.cover_image_url && (
          <div className="mb-8 rounded-xl overflow-hidden">
            <img
              src={post.cover_image_url}
              alt={post.title}
              className="w-full h-auto object-cover"
            />
          </div>
        )}

        {/* Content */}
        <div className="prose prose-lg dark:prose-invert max-w-none">
          {renderContent()}
        </div>

        {/* Tags and Share */}
        <div className="mt-12 pt-8 border-t">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            {post.tags && post.tags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {post.tags.map((tag) => (
                  <Badge key={tag} variant="outline" className="flex items-center gap-1">
                    <Tag className="h-3 w-3" />
                    {tag}
                  </Badge>
                ))}
              </div>
            )}
            <SocialShareButtons 
              url={`https://calclab.app/blog/${post.slug}`}
              title={post.title}
              description={post.excerpt}
            />
          </div>
        </div>

        {/* Related Posts CTA */}
        <div className="mt-12 p-8 bg-muted/50 rounded-xl text-center">
          <h3 className="text-xl font-semibold mb-2">Explore More</h3>
          <p className="text-muted-foreground mb-4">
            Discover more educational content on CalcLab
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Button asChild>
              <Link to="/blog">More Articles</Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/supercalc">Try SuperCalc AI</Link>
            </Button>
          </div>
        </div>
      </article>
    </PageLayout>
  );
}
